import React, { useEffect, useState } from 'react';
import { useParams, useLoaderData, useSearchParams } from 'react-router-dom';
import { Dropdown } from '../../components/PrimereactComponents';
import { useTranslation } from 'react-i18next';
import MemberCardWrapper from '../../components/common/MemberCardWrapper/MemberCardWrapper';
import { Colors, URLKeys } from '../../Constants';
import Intro from '../../components/common/Intro/Intro';
import Section from '../../components/common/Section/Section';
import { DropdownChangeParams } from 'primereact/dropdown';
import { DropdownOptionType, MemberDataResponseType, PartiesDataResponseType } from '../../types';

function transformMemberData(member: MemberDataResponseType, partiesData: PartiesDataResponseType[]) {
    const nameParts = member.jmeno.split(' ');
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ');

    // Should probably be done on BE
    let newPartyData = null;
    if (!member.aktivni) {
        partiesData.forEach((party) => {
            const foundMember = party.clenove.find((clen) => clen.id === member.id && clen.aktivni);
            if (foundMember && foundMember.aktivni) {
                newPartyData = {
                    partyName: party.strana_zkratka,
                    partyColor: party.strana_barva
                };
            }
        });
    }

    return {
        id: member.id,
        firstName: firstName,
        lastName: lastName,
        inactive: !member.aktivni,
        gender: member.pohlavi,
        newPartyData
    };
}
const findOptionByCode = (arrayOfOptions: DropdownOptionType[], codeToFind: number) => {
    return arrayOfOptions.find((option) => option.code === codeToFind) || undefined;
};

function Members() {
    const { t } = useTranslation();
    const { bodyType, zastupitelstvoId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const response = useLoaderData() as any[]; // Should be replaced by generated api model type
    const basicData = response[0].data;
    const partiesData = response[1].data as PartiesDataResponseType[];
    // const partiesStats = response[2].data; Bude odstraněno, obsahuje navíc data, která nejsou potřeba
    const municipality = response[3].data;
    const partyOptions = response[4].data as DropdownOptionType[];
    const memberOptions = response[5].data as DropdownOptionType[];

    const [partyOptionsForSpecificMember, setPartyOptionsForSpecificMember] = useState<DropdownOptionType[] | null>(
        null
    );
    const [memberOptionsForSpecificParty, setMemberOptionsForSpecificParty] = useState<DropdownOptionType[] | null>(
        null
    );

    const [filteringState, setFilteringState] = useState({
        member: findOptionByCode(memberOptions, Number(searchParams.get(URLKeys.member)) || 0) || null,
        selectedParty: findOptionByCode(partyOptions, Number(searchParams.get(URLKeys.party)) || 0) || null
    });

    const handleFilteringChange = (e: DropdownChangeParams, key: keyof typeof filteringState) => {
        const newFilteringState = { ...filteringState, [key]: e.value };
        setFilteringState(newFilteringState);

        setSearchParams(
            (prevParams) => {
                const newParams = new URLSearchParams(prevParams);
                if (newFilteringState.selectedParty) {
                    newParams.set(URLKeys.party, newFilteringState.selectedParty.code.toString());
                } else {
                    newParams.delete(URLKeys.party);
                }
                if (newFilteringState.member) {
                    newParams.set(URLKeys.member, newFilteringState.member.code.toString());
                } else {
                    newParams.delete(URLKeys.member);
                }
                return newParams;
            },
            { replace: true }
        );
    };

    useEffect(() => {
        if (filteringState?.member?.code) {
            setPartyOptionsForSpecificMember(
                partiesData
                    .filter((party) => {
                        return party.clenove.find(
                            (member) => Number(member.id) === Number(filteringState.member?.code)
                        );
                    })
                    .map((party) => ({
                        name: party.strana_zkratka,
                        code: party.strana_id
                    }))
            );
        } else {
            setPartyOptionsForSpecificMember(null);
        }
    }, [filteringState?.member, partiesData]);

    useEffect(() => {
        if (filteringState?.selectedParty?.code) {
            const selectedParty = partiesData.find(
                (party) => Number(party.strana_id) === Number(filteringState.selectedParty?.code)
            );
            if (selectedParty) {
                setMemberOptionsForSpecificParty(
                    selectedParty.clenove.map((member) => ({
                        name: member.jmeno,
                        code: member.id
                    }))
                );
            } else {
                setMemberOptionsForSpecificParty(null);
            }
        } else {
            setMemberOptionsForSpecificParty(null);
        }
    }, [filteringState?.selectedParty, partiesData]);

    if (
        basicData !== undefined &&
        partiesData !== undefined &&
        // partiesStats !== undefined &&
        zastupitelstvoId !== undefined &&
        municipality !== undefined &&
        partyOptions !== null
    ) {
        // should be done on BE?
        partiesData.forEach((party) => {
            let active_party = basicData.nejnovejsi_zasedani.mandaty.find(
                (obj: PartiesDataResponseType) => obj.strana_id === party.strana_id
            );
            if (active_party) {
                party['aktivni'] = true;
                party['pocet_mandatu'] = active_party.pocet_mandatu;
            } else {
                party['aktivni'] = false;
                party['pocet_mandatu'] = 0;
            }
        });

        const filteringComponents = (
            <>
                <span className='p-float-label'>
                    <Dropdown
                        showClear
                        inputId='member'
                        value={filteringState.member}
                        options={
                            filteringState.selectedParty ? memberOptionsForSpecificParty || [] : memberOptions || []
                        }
                        onChange={(e) => handleFilteringChange(e, 'member')}
                        optionLabel='name'
                        filter
                        filterBy='name'
                    />
                    <label htmlFor='member'>{t('select-member')}</label>
                </span>
                <span className='p-float-label'>
                    <Dropdown
                        showClear
                        inputId='selectedParty'
                        value={filteringState.selectedParty}
                        options={filteringState.member ? partyOptionsForSpecificMember || [] : partyOptions || []}
                        onChange={(e) => handleFilteringChange(e, 'selectedParty')}
                        optionLabel='name'
                        filter
                        filterBy='name'
                    />
                    <label htmlFor='selectedParty'>{t('select-party')}</label>
                </span>
            </>
        );

        const selectedBodyTypeData = bodyType ? municipality.organy[bodyType] : undefined;
        const totalCountOfMembersInThisTerm = basicData.pocet_zastupitelu + basicData.pocet_zastupitelek;

        return (
            <>
                <Intro
                    title={t('Members')}
                    description={t('in-term-composition', {
                        ns: 'translation',
                        total_mandates: selectedBodyTypeData.pocet_zastupitelu,
                        parties_count: partiesData.length,
                        members_count: totalCountOfMembersInThisTerm,
                        type: selectedBodyTypeData.nazev
                    })}
                />
                <Section contentClassName='filtering'>{filteringComponents}</Section>
                {partiesData.map((party, i) => {
                    if (
                        filteringState.member &&
                        !party.clenove.find((member) => member.id === filteringState.member?.code)
                    ) {
                        return null;
                    }
                    if (filteringState.selectedParty && party.strana_id !== filteringState.selectedParty.code) {
                        return null;
                    }
                    let memberGroupsData = [];
                    if (party.pocet_aktivnich > 0) {
                        memberGroupsData.push({
                            partyName: party.strana_zkratka,
                            customCountText: t('active-members'),
                            partyColor: party.strana_barva,
                            count: party.pocet_aktivnich,
                            members: party.clenove
                                .filter((clen) => clen.aktivni)
                                .map((clen) => transformMemberData(clen, partiesData))
                        });
                    }
                    if (party.pocet_neaktivnich > 0) {
                        memberGroupsData.push({
                            partyName: party.strana_zkratka,
                            customCountText: t('history'),
                            partyColor: Colors.darkGray,
                            count: party.pocet_neaktivnich,
                            members: party.clenove
                                .filter((clen) => !clen.aktivni)
                                .map((clen) => transformMemberData(clen, partiesData))
                        });
                    }

                    return (
                        <MemberCardWrapper
                            groupName={party.strana_zkratka}
                            count={party.pocet_mandatu}
                            chipText={t('mandate', { count: party.pocet_mandatu })}
                            groupCountText=''
                            bgColor={party.strana_barva}
                            partyId={party.strana_id}
                            expanded
                            memberGroupsData={memberGroupsData}
                            key={i}
                            tabIndex={i}
                        />
                    );
                })}
            </>
        );
    }
}

export default Members;
